/**
 * Sidebar Permission Utilities
 *
 * Utilities to get permission configuration for sidebar navigation items.
 * Sidebar permissions are derived from ROUTE_PERMISSIONS since the sidebar
 * is just quick navigation to routes - if you can't access a route, you
 * shouldn't see its navigation item.
 */

import { Permission } from 'features/roles';
import {
  SIDEBAR_ROUTE_MAP,
  ROUTE_PERMISSIONS,
  RoutePermissionConfig,
} from 'config/permissions.config';

/**
 * Gets the route path for a sidebar item.
 *
 * @param itemId - The sidebar item ID (e.g., 'projects', 'leads')
 * @returns The corresponding route path or undefined
 */
export const getSidebarItemRoute = (itemId: string): string | undefined => {
  return SIDEBAR_ROUTE_MAP[itemId];
};

/**
 * Gets the permission configuration for a sidebar item by looking up
 * its corresponding route in ROUTE_PERMISSIONS.
 *
 * @param itemId - The sidebar item ID (e.g., 'projects', 'leads', 'inventory')
 * @returns The route permission configuration or undefined if not found
 *
 * @example
 * const config = getSidebarItemConfig('projects');
 * // { permission: Permission.VIEW_PROJECTS }
 */
export const getSidebarItemConfig = (itemId: string): RoutePermissionConfig | undefined => {
  const route = getSidebarItemRoute(itemId);
  if (!route) return undefined;
  return ROUTE_PERMISSIONS[route];
};

/**
 * Gets just the required permission(s) for a sidebar item.
 * Derived from the corresponding route's permission configuration.
 *
 * @param itemId - The sidebar item ID
 * @returns The required permission(s) or undefined
 *
 * @example
 * const permission = getSidebarItemPermission('projects');
 * // Permission.VIEW_PROJECTS
 */
export const getSidebarItemPermission = (
  itemId: string,
): Permission | Permission[] | undefined => {
  const config = getSidebarItemConfig(itemId);
  return config?.permission;
};

/**
 * Checks if a sidebar item is admin-only.
 * Derived from the corresponding route's adminOnly flag.
 *
 * @param itemId - The sidebar item ID
 * @returns True if the item requires admin access
 *
 * @example
 * isSidebarItemAdminOnly('company'); // true
 * isSidebarItemAdminOnly('projects'); // false
 */
export const isSidebarItemAdminOnly = (itemId: string): boolean => {
  const config = getSidebarItemConfig(itemId);
  return config?.adminOnly === true;
};

/**
 * Gets all sidebar item IDs that are configured.
 *
 * @returns Array of sidebar item IDs
 */
export const getAllSidebarItemIds = (): string[] => {
  return Object.keys(SIDEBAR_ROUTE_MAP);
};
